import { useAccessFlowUserFormContext } from "./AccessManagementUserFormProvider";
import { Attention, Input, Typography } from "@optimizely/axiom";
import { AccessManagementUserFormIconButton } from "./AccessManagementUserFormIconButton";
import styles from "./AccessManagementUserForm.module.scss";
import React, { useEffect, useState } from "react";
import { useUsers } from "../../../../hooks/useUsers/useUsers";

export const UserFormNameFields = ({
    disableEdit = false,
    hideLastName = false,
    user
}: {
    disableEdit?: boolean;
    /**
     * Collapses the two name fields into a single "Name" field showing only the first name.
     * Used for virtual teammates, which are provisioned with a placeholder last name that
     * should not be surfaced to admins. The stored last name is still preserved on save.
     */
    hideLastName?: boolean;
    user: {
        email: string;
        firstName: string;
        lastName: string;
    };
}) => {
    const { email, firstName: initialFirstName, lastName: initialLastName } = user;
    const { updateUserFields } = useUsers();
    const { activeField, setActiveField, loading, setLoadingState } = useAccessFlowUserFormContext();
    const [error, setError] = useState(null);
    const isEditing = (activeField?.name as string) === "name";
    const [firstName, updateFirstName] = useState(initialFirstName);
    const [lastName, updateLastName] = useState(initialLastName);
    const firstNameLabel = hideLastName ? "Name" : "First Name";
    const onEditClick = () => {
        setActiveField({ name: "name", hasChanged: false });
    };

    useEffect(() => {
        updateFirstName(initialFirstName);
        updateLastName(initialLastName);
    }, [email, initialFirstName, initialLastName]);

    const onSave = () => {
        setLoadingState(true);
        if (error) {
            setError(null);
        }
        updateUserFields({
            email,
            firstName,
            lastName
        })
            .then(() => {
                setActiveField({ name: null, hasChanged: false });
            })
            .catch(([e]) => {
                console.error("Failed updating user: ", e);
                setError(e.message);
            })
            .finally(() => {
                setLoadingState(false);
            });
    };

    const revertChanges = () => {
        updateFirstName(initialFirstName);
        updateLastName(initialLastName);
    };

    useEffect(() => {
        window.document.addEventListener("confirmation-dialog:name:discard-changes", revertChanges);
        return () => {
            window.document.removeEventListener("confirmation-dialog:name:discard-changes", revertChanges);
        };
    });

    return (
        <div className={`${styles["access-management-user-form"]}`}>
            {error && (
                <Attention type="bad-news" className="push--right push--bottom">
                    {error}
                </Attention>
            )}
            <div
                className={`${styles["user-form-name-grid"]} ${
                    hideLastName ? styles["user-form-name-grid--single"] : ""
                }`}
            >
                <div>
                    <label className={`${styles["user-form-field-label__top-line"]} oui-label`}>{firstNameLabel}</label>
                    {isEditing ? (
                        <Input
                            className="flex--1"
                            type="text"
                            onChange={(e) => {
                                updateFirstName(e.target.value);
                                setActiveField({ name: "name", hasChanged: true });
                            }}
                            isDisabled={loading}
                            value={firstName}
                            placeholder={firstNameLabel}
                        />
                    ) : (
                        <Typography type="body" className="label--disabled">
                            {firstName}
                        </Typography>
                    )}
                </div>
                {!hideLastName && (
                    <div>
                        <label className={`${styles["user-form-field-label__top-line"]} oui-label`}>Last Name</label>
                        {isEditing ? (
                            <Input
                                className="flex--1"
                                type="text"
                                onChange={(e) => {
                                    updateLastName(e.target.value);
                                    setActiveField({ name: "name", hasChanged: true });
                                }}
                                isDisabled={loading}
                                value={lastName}
                                placeholder="Last Name"
                            />
                        ) : (
                            <Typography type="body" className="label--disabled">
                                {lastName}
                            </Typography>
                        )}
                    </div>
                )}
                <div className={styles["user-form-name-grid__action"]}>
                    {isEditing ? (
                        <AccessManagementUserFormIconButton
                            title="Save"
                            loading={loading}
                            icon="check"
                            onClick={onSave}
                        />
                    ) : disableEdit ? null : (
                        <AccessManagementUserFormIconButton title="Edit" icon="pen-to-square" onClick={onEditClick} />
                    )}
                </div>
            </div>
        </div>
    );
};

UserFormNameFields.displayName = "UserFormNameFields";
